import { isApolloError } from '@apollo/client/errors';
import {
  addBreadcrumb,
  captureException,
  captureMessage,
  ErrorEvent,
  EventHint,
  SeverityLevel,
} from '@sentry/nextjs';

type Breadcrumb = {
  level: SeverityLevel;
  message: string;
  category?: string;
};

export const captureBreadcrumb = (breadcrumb: Breadcrumb) => {
  addBreadcrumb({
    category: breadcrumb.category,
    message: breadcrumb.message,
    level: breadcrumb.level,
  });
};

export const captureError = captureException;

export const captureErrorMessage = captureMessage;

// An ApolloError's stack only has Apollo frames, so Sentry groups every failed
// operation into one issue. The Apollo error link records which operation each
// GraphQL/network error came from, and tagApolloError puts it on the event.
const operationNames = new WeakMap<object, string>();

export const rememberApolloOperation = (
  error: object,
  operationName: string,
) => {
  operationNames.set(error, operationName);
};

export const tagApolloError = (event: ErrorEvent, hint: EventHint) => {
  const error = hint.originalException;
  if (!(error instanceof Error) || !isApolloError(error)) return event;

  const source = [error.networkError, ...error.graphQLErrors].find(
    (err) => err && operationNames.has(err),
  );
  const operationName = source ? operationNames.get(source) : undefined;
  if (!operationName) return event;

  return {
    ...event,
    tags: { ...event.tags, 'graphql.operation': operationName },
    fingerprint: ['ApolloError', operationName],
  };
};
